import SEOHead from "@/components/SEOHead";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import ReminderPausePanel from "@/components/ReminderPausePanel";
import WhatsAppOptInPanel from "@/components/WhatsAppOptInPanel";

const LEAD_TIME_OPTIONS = [
  { value: "1_day", label: "1 day before" },
  { value: "3_days", label: "3 days before" },
  { value: "1_week", label: "1 week before" },
];

const SettingsPreferences = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reminderLeadTime, setReminderLeadTime] = useState("3_days");
  const [initialLeadTime, setInitialLeadTime] = useState("3_days");
  const [emailSubscribed, setEmailSubscribed] = useState(true);
  const [togglingEmail, setTogglingEmail] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState<string | null>(null);
  const [whatsappConfirmed, setWhatsappConfirmed] = useState(false);
  const [globalOccasionReminders, setGlobalOccasionReminders] = useState(true);
  const [togglingGlobal, setTogglingGlobal] = useState(false);
  const [showPausePanel, setShowPausePanel] = useState(false);
  const [pausedUntil, setPausedUntil] = useState<string | null>(null);
  const [pausedAt, setPausedAt] = useState<string | null>(null);

  const hasChanges = reminderLeadTime !== initialLeadTime;
  const isPaused = pausedUntil && new Date(pausedUntil) > new Date();

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("reminder_lead_time, email_subscribed, whatsapp_number, whatsapp_confirmed, reminders_paused_until, reminders_paused_at, global_occasion_reminders")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) {
        const lt = data.reminder_lead_time ?? "3_days";
        setReminderLeadTime(lt);
        setInitialLeadTime(lt);
        setEmailSubscribed(data.email_subscribed ?? true);
        setWhatsappNumber(data.whatsapp_number ?? null);
        setWhatsappConfirmed(data.whatsapp_confirmed ?? false);
        setGlobalOccasionReminders(data.global_occasion_reminders ?? true);
        setPausedUntil(data.reminders_paused_until ?? null);
        setPausedAt(data.reminders_paused_at ?? null);
      }
      setLoading(false);
    };
    load();
  }, [user]);

  const handleEmailToggle = (checked: boolean) => {
    if (checked) {
      // Turning ON — if paused, clear pause
      if (isPaused) {
        resumeReminders();
      }
      // If fully off, re-subscribe
      if (!emailSubscribed) {
        updateEmailSubscription(true);
      }
    } else {
      // Turning OFF — show inline pause panel
      setShowPausePanel(true);
    }
  };

  const resumeReminders = async () => {
    if (!user) return;
    setTogglingEmail(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        reminders_paused_until: null,
        reminders_paused_at: null,
      })
      .eq("user_id", user.id);
    setTogglingEmail(false);
    if (!error) {
      setPausedUntil(null);
      setPausedAt(null);
      toast.success("Reminders resumed 💛");
    } else {
      toast.error("That didn't work — give it another go.");
    }
  };

  const handlePauseConfirm = async (days: number) => {
    if (!user) return;
    setTogglingEmail(true);
    const pauseDate = new Date();
    pauseDate.setDate(pauseDate.getDate() + days);
    const { error } = await supabase
      .from("profiles")
      .update({
        reminders_paused_until: pauseDate.toISOString(),
        reminders_paused_at: new Date().toISOString(),
        email_subscribed: true,
      })
      .eq("user_id", user.id);
    setTogglingEmail(false);
    if (!error) {
      setPausedUntil(pauseDate.toISOString());
      setPausedAt(new Date().toISOString());
      setShowPausePanel(false);
      toast.success("Reminders paused 💛");
    } else {
      toast.error("That didn't work — give it another go.");
    }
  };

  const handleTurnOffEntirely = async () => {
    setShowPausePanel(false);
    setTogglingEmail(true);
    try {
      const { error } = await supabase.functions.invoke("update-email-subscription", {
        body: { subscribed: false },
      });
      if (error) throw error;
      setEmailSubscribed(false);
      setPausedUntil(null);
      setPausedAt(null);
      setShowPausePanel(false);
    } catch {
      toast.error("That didn't work — give it another go.");
    } finally {
      setTogglingEmail(false);
    }
  };

  const updateEmailSubscription = async (subscribed: boolean) => {
    setTogglingEmail(true);
    try {
      const { error } = await supabase.functions.invoke("update-email-subscription", {
        body: { subscribed },
      });
      if (error) throw error;
      setEmailSubscribed(subscribed);
      if (subscribed) {
        toast.success("Email reminders turned back on");
      }
    } catch {
      toast.error("That didn't work — give it another go.");
    } finally {
      setTogglingEmail(false);
    }
  };

  const reloadWhatsAppState = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("profiles")
      .select("whatsapp_number, whatsapp_confirmed")
      .eq("user_id", user.id)
      .maybeSingle();
    if (data) {
      setWhatsappNumber(data.whatsapp_number ?? null);
      setWhatsappConfirmed(data.whatsapp_confirmed ?? false);
    }
  };

  const handleGlobalOccasionToggle = async (checked: boolean) => {
    if (!user) return;
    setTogglingGlobal(true);
    const { error } = await supabase
      .from("profiles")
      .update({ global_occasion_reminders: checked })
      .eq("user_id", user.id);
    setTogglingGlobal(false);
    if (!error) {
      setGlobalOccasionReminders(checked);
      toast.success(checked ? "Global occasion reminders turned on 💛" : "Global occasion reminders turned off");
    } else {
      toast.error("That didn't work — give it another go.");
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ reminder_lead_time: reminderLeadTime })
      .eq("user_id", user.id);
    setSaving(false);
    if (error) {
      toast.error("That didn't work — give it another go.");
    } else {
      setInitialLeadTime(reminderLeadTime);
      toast.success("Preferences saved.");
    }
  };

  // Determine toggle visual state
  const toggleChecked = emailSubscribed && !isPaused;

  if (loading) {
    return (
      <>
        <SEOHead title="Preferences — Dearestly." description="Manage your Dearestly. reminder and notification preferences." path="/settings/preferences" noindex />
        <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    </>
    );
  }

  return (
    <>
      <SEOHead title="Preferences — Dearestly." description="Manage your Dearestly. reminder and notification preferences." path="/settings/preferences" noindex />
      <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 md:px-6 py-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-page-title text-foreground">My Preferences</h1>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-6 px-4 md:px-6 py-8 pb-24 animate-fade-in">
        <div className="card-premium p-6 space-y-6">
          {/* Lead time */}
          <div className="space-y-2">
            <Label>Reminder lead time</Label>
            <Select value={reminderLeadTime} onValueChange={setReminderLeadTime}>
              <SelectTrigger className="rounded-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEAD_TIME_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notification channels */}
          <div className="space-y-3">
            <Label>Notification channels</Label>

            {/* Email toggle */}
            <div className={`rounded-2xl border border-border bg-card p-4 space-y-2 transition-opacity ${!emailSubscribed && !isPaused ? 'opacity-50' : ''}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">Email Reminders</p>
                  <p className="text-xs text-muted-foreground">Receive birthday reminders by email</p>
                </div>
                <Switch
                  checked={toggleChecked}
                  onCheckedChange={handleEmailToggle}
                  disabled={togglingEmail}
                  className={
                    isPaused
                      ? "data-[state=unchecked]:bg-[#C9A84C] data-[state=checked]:bg-accent"
                      : "data-[state=checked]:bg-accent"
                  }
                />
              </div>

              {/* Paused state info */}
              {isPaused && !showPausePanel && (
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs" style={{ color: "#C9A84C" }}>
                    Paused until {format(new Date(pausedUntil!), "d MMM")}
                  </p>
                  <button
                    onClick={resumeReminders}
                    disabled={togglingEmail}
                    className="text-xs text-muted-foreground underline hover:text-foreground transition-colors"
                  >
                    Resume now
                  </button>
                </div>
              )}

              {/* Normal states */}
              {!isPaused && !showPausePanel && (
                <>
                  {emailSubscribed ? (
                    <p className="text-xs text-muted-foreground">
                      Sending from hello@circle.dearestly.co
                    </p>
                  ) : (
                    <p className="text-xs text-destructive">
                      You won't receive birthday reminder emails while this is off.
                    </p>
                  )}
                </>
              )}

              {/* Inline pause panel */}
              {showPausePanel && (
                <ReminderPausePanel
                  loading={togglingEmail}
                  onPause={handlePauseConfirm}
                  onTurnOff={handleTurnOffEntirely}
                  onCancel={() => setShowPausePanel(false)}
                />
              )}
            </div>

            {/* WhatsApp */}
            <WhatsAppOptInPanel
              whatsappNumber={whatsappNumber}
              whatsappConfirmed={whatsappConfirmed}
              onStateChange={reloadWhatsAppState}
            />
          </div>

          {/* Global occasion reminders */}
          <div className="space-y-2">
            <Label>Global occasion reminders</Label>
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">Global Occasion Reminders</p>
                  <p className="text-xs text-muted-foreground">
                    Receive email reminders for Valentine's Day, Mother's Day, Christmas, and more.
                  </p>
                </div>
                <Switch
                  checked={globalOccasionReminders}
                  onCheckedChange={handleGlobalOccasionToggle}
                  disabled={togglingGlobal}
                  className="data-[state=checked]:bg-accent"
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Fixed bottom save button */}
      <div className="fixed bottom-0 left-0 right-0 z-10">
        <div className="h-8 bg-gradient-to-t from-background to-transparent" />
        <div className="bg-background px-4 pb-6">
          <div className="mx-auto max-w-2xl">
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="w-full rounded-full"
              size="lg"
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </>
  );
};

export default SettingsPreferences;
